<!-- Quality_Gap_Detail.vue -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 p-5">
    <!-- Back + Title -->
    <div class="flex items-center gap-2 mb-4">
      <button
        @click="$emit('close')"
        class="p-1 -ml-1 text-gray-400 hover:text-gray-600 transition-colors rounded hover:bg-gray-100"
        title="Back to gaps"
      >
        <FontAwesomeIcon :icon="['fas', 'arrow-left']" class="text-xs" />
      </button>
      <div>
        <div class="text-sm font-semibold text-gray-900">{{ gap.measure_name || 'Care Gap' }}</div>
        <div class="flex items-center gap-2 mt-0.5">
          <span v-if="gap.measure_id" class="text-xs text-gray-500">{{ gap.measure_id }}</span>
          <span
            :class="[
              'px-1.5 py-px text-[10px] rounded-full font-medium',
              gap.status === 'potentially_closed' ? 'bg-amber-100 text-amber-700' :
              gap.status === 'open' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
            ]"
          >
            {{ gap.status === 'potentially_closed' ? 'Potentially Closed' :
               gap.status === 'open' ? 'Open' : 'Closed' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Gap Details -->
    <div class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Gap Details</div>
      <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <span class="text-xs text-gray-500">Due Date</span>
        <span class="text-xs text-gray-900">{{ formatDate(gap.due_date) || 'Not set' }}</span>
        <template v-if="gap.contract_name">
          <span class="text-xs text-gray-500">Contract</span>
          <span class="sensitive text-xs text-gray-900">{{ gap.contract_name }}</span>
        </template>
        <template v-if="gap.payer_name">
          <span class="text-xs text-gray-500">Payer</span>
          <span class="sensitive text-xs text-gray-900">{{ gap.payer_name }}</span>
        </template>
        <template v-if="gap.lob_name || gap.lob">
          <span class="text-xs text-gray-500">LOB</span>
          <span class="text-xs text-gray-900">{{ gap.lob_name || gap.lob }}</span>
        </template>
        <template v-if="daysSinceDos !== null">
          <span class="text-xs text-gray-500">Days Since DOS</span>
          <span :class="['text-xs font-medium', daysSinceDos > 90 ? 'text-red-600' : daysSinceDos > 45 ? 'text-orange-600' : 'text-gray-900']">
            {{ daysSinceDos }} days
          </span>
        </template>
        <template v-if="gap.evidence_value">
          <span class="text-xs text-gray-500">Evidence Value</span>
          <span class="text-xs text-gray-900">{{ gap.evidence_value }}</span>
        </template>
      </div>
      <div v-if="gap.description" class="mt-2 pt-2 border-t border-gray-100">
        <DemoText tag="p" class="text-xs text-gray-600" :text="gap.description" :record="commentPatientIdentifier" />
      </div>
    </div>

    <!-- Clinical Evidence -->
    <div class="pt-3 border-t border-gray-100 mb-4">
      <Clinical_Evidence_Widget
        :patient-identifier="patient?.patient_identifier || ''"
        :gap="gap"
      />
    </div>

    <!-- Patient Info -->
    <div v-if="hasPatientInfo" class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Patient Info</div>
      <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <template v-if="gap.bene_phone">
          <span class="text-xs text-gray-500">Phone</span>
          <span class="text-xs text-gray-900 flex items-center gap-1">
            <span class="sensitive">{{ gap.bene_phone }}</span>
            <button @click="copyToClipboard(gap.bene_phone)" class="text-gray-400 hover:text-gray-600" title="Copy">
              <FontAwesomeIcon :icon="['fas', 'copy']" class="text-[10px]" />
            </button>
          </span>
        </template>
        <template v-if="formattedAddress">
          <span class="text-xs text-gray-500">Address</span>
          <span class="sensitive text-xs text-gray-900">{{ formattedAddress }}</span>
        </template>
        <template v-if="gap.provider_name">
          <span class="text-xs text-gray-500">Provider</span>
          <span class="sensitive text-xs text-gray-900">{{ gap.provider_name }}</span>
        </template>
        <template v-if="gap.provider_address">
          <span class="text-xs text-gray-500">Provider Address</span>
          <span class="sensitive text-xs text-gray-900">{{ gap.provider_address }}</span>
        </template>
        <template v-if="gap.language">
          <span class="text-xs text-gray-500">Language</span>
          <span class="text-xs text-gray-900">{{ gap.language }}</span>
        </template>
        <template v-if="gap.last_service_date">
          <span class="text-xs text-gray-500">Last Service Date</span>
          <span class="text-xs text-gray-900">{{ formatDate(gap.last_service_date) }}</span>
        </template>
      </div>
    </div>

    <!-- Medication Adherence Detail -->
    <div v-if="isMedAdherence" class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Medication Details</div>
      <Task_MedicationAdherence
        :task-type="gap.task_type_id"
        :task-type-name="'Medication Adherence Risk'"
        :task-metadata="medAdherenceMetadata"
        :opportunities="gap.opportunity ? [gap.opportunity] : []"
      />
    </div>

    <!-- Pharmacy Fills (Surescripts) -->
    <div v-if="isMedAdherence" class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Pharmacy Activity</div>

      <!-- Not enrolled: the common case, and it must not read as an error -->
      <div v-if="!fill" class="text-xs text-gray-500 py-1">
        No pharmacy data — this patient is not enrolled in pharmacy monitoring.
      </div>

      <!-- Enrolled, but no fill of this drug on record -->
      <div v-else-if="!fill.has_pharmacy_data" class="text-xs text-gray-500 py-1">
        No pharmacy fill on record for this medication.
      </div>

      <template v-else>
        <!-- The actionable case: pharmacy is ahead of the payer file -->
        <div
          v-if="fill.is_newer_than_payer"
          class="flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 mb-2"
        >
          <FontAwesomeIcon :icon="['fas', 'circle-check']" class="text-emerald-600 text-xs mt-0.5" />
          <div class="text-xs text-emerald-900">
            <span class="font-medium">Filled {{ formatDate(fill.last_filled_dt) }}</span>
            — {{ fill.days_newer_than_payer }} day{{ fill.days_newer_than_payer === 1 ? '' : 's' }}
            after the payer's last recorded fill. Patient may not need outreach.
          </div>
        </div>

        <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <span class="text-xs text-gray-500">Medication</span>
          <span class="text-xs text-gray-900">{{ fill.drug_name || '—' }}</span>

          <span class="text-xs text-gray-500">Last Filled</span>
          <span class="text-xs text-gray-900">
            {{ formatDate(fill.last_filled_dt) }}
            <span v-if="fill.days_since_fill != null" class="text-gray-500">
              ({{ fill.days_since_fill }}d ago)
            </span>
          </span>

          <template v-if="fill.days_supply">
            <span class="text-xs text-gray-500">Days Supply</span>
            <span class="text-xs text-gray-900">{{ fill.days_supply }}</span>
          </template>

          <template v-if="fill.projected_runout_dt">
            <span class="text-xs text-gray-500">Runs Out</span>
            <span class="text-xs text-gray-900">{{ formatDate(fill.projected_runout_dt) }}</span>
          </template>

          <template v-if="fill.pharmacy_name">
            <span class="text-xs text-gray-500">Pharmacy</span>
            <span class="text-xs text-gray-900">
              <span class="sensitive" data-spoof-kind="facility">{{ fill.pharmacy_name }}</span>
              <span v-if="fill.pharmacy_phone" class="text-gray-500">· {{ fill.pharmacy_phone }}</span>
            </span>
          </template>
        </div>

        <p class="text-[11px] text-gray-400 mt-1.5 leading-snug">
          A fill confirms the pharmacy dispensed the medication; it does not confirm
          the patient collected it. Typically 3–5 days behind the pharmacy.
        </p>
      </template>
    </div>

    <!-- Task / Workflow -->
    <div v-if="gap.task_id" class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Task</div>

      <div v-if="taskLoading" class="flex items-center gap-2 py-2">
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-gray-400 animate-spin text-xs" />
        <span class="text-xs text-gray-500">Loading task...</span>
      </div>

      <template v-else-if="task">
        <!-- Stage / status / due -->
        <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 mb-3">
          <span class="text-xs text-gray-500">Stage</span>
          <span class="text-xs text-gray-900 font-medium">{{ task.task_type_name || '—' }}</span>
          <span class="text-xs text-gray-500">Status</span>
          <span class="text-xs text-gray-900">
            {{ taskStatusLabel }}
            <span
              v-if="taskIsOverdue"
              class="ml-1 px-1.5 py-px text-[10px] rounded-full font-medium bg-red-100 text-red-700"
            >Overdue</span>
          </span>
          <template v-if="task.due_date">
            <span class="text-xs text-gray-500">Task Due</span>
            <span class="text-xs text-gray-900">{{ formatDate(task.due_date) }}</span>
          </template>
        </div>

        <!-- Submit an action -->
        <div v-if="activeActions.length">
          <label class="block text-xs text-gray-500 mb-1">Action</label>
          <select
            v-model="selectedAction"
            class="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white
                   focus:ring-2 focus:ring-blue-dark focus:border-transparent
                   hover:border-gray-400 transition-colors"
          >
            <option disabled value="">Select an action...</option>
            <option v-for="a in activeActions" :key="a.id" :value="a">{{ a.name }}</option>
          </select>

          <!-- Date needed for snooze / update-due-date -->
          <div v-if="actionNeedsDate" class="mt-2">
            <label class="block text-xs text-gray-500 mb-1">
              {{ selectedAction.task_instruction_type_id === 16 ? 'New due date' : 'Snooze until' }}
            </label>
            <div class="flex items-center gap-1.5">
              <button
                v-for="opt in datePresets"
                :key="opt.days"
                type="button"
                class="px-2 py-1 rounded text-[11px] font-medium transition-colors"
                :class="daysSelected === opt.days
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'"
                @click="setQuickDate(opt.days)"
              >{{ opt.label }}</button>
              <input
                v-model="actionDate"
                type="datetime-local"
                class="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                @change="daysSelected = null"
              />
            </div>
          </div>

          <textarea
            v-model="actionNotes"
            rows="2"
            placeholder="Notes (optional)"
            class="mt-2 w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs
                   focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          ></textarea>

          <div v-if="actionError" class="mt-2 text-xs text-red-600">{{ actionError }}</div>

          <button
            type="button"
            :disabled="!canSubmitAction"
            class="mt-2 w-full px-3 py-1.5 rounded text-xs font-medium transition-colors"
            :class="canSubmitAction
              ? 'bg-primary-500 text-white hover:bg-primary-600'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'"
            @click="submitTaskAction"
          >
            {{ submittingAction ? 'Submitting...' : 'Submit Action' }}
          </button>
        </div>
        <div v-else class="text-xs text-gray-400">
          No actions available for this task.
        </div>

        <!-- Action history -->
        <div v-if="task.task_prior_actions && task.task_prior_actions.length" class="mt-3 pt-3 border-t border-gray-100">
          <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Action History ({{ task.task_prior_actions.length }})
          </div>
          <Task_Actions_List :task-prior-actions="task.task_prior_actions" />
        </div>
      </template>

      <div v-else-if="taskError" class="text-xs text-red-600">
        {{ taskError }}
        <button @click="fetchTask" class="ml-1 underline hover:text-red-700">Retry</button>
      </div>
    </div>

    <!-- Log Work -->
    <div class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Log Work</div>
      <WorkLogger
        v-if="gap.opportunity_id"
        :opportunities="[gap.opportunity]"
        :task-type-id="getTaskTypeId()"
        :task-id="gap.task_id"
        :initial-form-data="gapFormDefaults"
        @work-submitted="onWorkSubmitted"
      />
      <div v-else class="text-xs text-gray-400">
        No opportunity linked to log work
      </div>
    </div>

    <!-- Work History -->
    <div class="pt-3 border-t border-gray-100">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Work History</div>
      <Work_Timeline
        v-if="gap.opportunity_id"
        ref="workTimeline"
        :opportunities="[gap.opportunity]"
        @work-deleted="onWorkDeleted"
      />
      <div v-else class="text-xs text-gray-400">
        No work history available
      </div>
    </div>

    <!-- Comment -->
    <div v-if="gap.entity_id && commentPatientIdentifier" class="pt-3 border-t border-gray-100 mt-4">
      <CommentWidget
        :patient-identifier="commentPatientIdentifier"
        :entity-id="gap.entity_id"
        :opportunity-id="gap.opportunity_id"
        @comment-saved="onCommentSaved"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import WorkLogger from '@/components/Portal/Task_Tables/Work_Widgets/WorkLogger.vue';
import Work_Timeline from '@/components/Portal/Task_Tables/Work_Widgets/Work_Timeline.vue';
import Task_MedicationAdherence from '@/components/Portal/Task_Tables/Task_Widgets/Task_MedicationAdherence.vue';
import Task_Actions_List from '@/components/Portal/Task_Tables/Task_Actions_List.vue';
import Clinical_Evidence_Widget from './Clinical_Evidence_Widget.vue';
import CommentWidget from '@/components/Portal/Task_Tables/Work_Widgets/CommentWidget.vue';
import { submitAction } from '@/services/api/tasks';
import { InstructionType, TaskStatus, NEEDS_DATE } from '@/constants/instructionTypes';
import { blockSensitiveEgress } from '@/utils/demoEgress';
import DemoText from '@/components/DemoMode/DemoText.vue';

export default {
  name: 'Quality_Gap_Detail',
  components: {
    DemoText,
    FontAwesomeIcon,
    WorkLogger,
    Work_Timeline,
    Task_MedicationAdherence,
    Task_Actions_List,
    Clinical_Evidence_Widget,
    CommentWidget
  },
  props: {
    gap: {
      type: Object,
      required: true
    },
    patient: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'refresh'],
  data() {
    return {
      task: null,
      taskLoading: false,
      taskError: null,
      selectedAction: '',
      actionNotes: '',
      actionDate: '',
      daysSelected: null,
      submittingAction: false,
      actionError: null,
      datePresets: [
        { days: 1, label: '1d' },
        { days: 3, label: '3d' },
        { days: 7, label: '7d' },
      ],
    };
  },
  computed: {
    taskStatusLabel() {
      const labels = {
        0: 'Unassigned', 1: 'Active', 2: 'Closed', 3: 'Complete',
        4: 'Snoozed', 5: 'Skipped', 6: 'Expired', 7: 'On Hold',
      };
      return labels[this.task?.status] || 'Unknown';
    },

    taskIsOverdue() {
      if (!this.task || this.task.status === TaskStatus.SNOOZED) return false;
      if (!this.task.due_date) return false;
      return new Date(this.task.due_date + 'T23:59:59') < new Date();
    },

    // Mirrors Task_Side_Panel/Patient_Task: hide actions that don't apply to
    // the task's current status, so the dropdown never offers a no-op.
    activeActions() {
      if (!this.task?.available_action_types) return [];
      if (![TaskStatus.ACTIVE, TaskStatus.SNOOZED, TaskStatus.ON_HOLD].includes(this.task.status)) return [];
      return this.task.available_action_types.filter((a) => {
        if (a.active === false) return false;
        if (a.task_instruction_type_id === InstructionType.UNSNOOZE && this.task.status !== TaskStatus.SNOOZED) return false;
        if (a.task_instruction_type_id === InstructionType.SNOOZE && this.task.status === TaskStatus.SNOOZED) return false;
        if (a.task_instruction_type_id === InstructionType.HOLD && this.task.status !== TaskStatus.ACTIVE) return false;
        if (a.task_instruction_type_id === InstructionType.RELEASE_HOLD && this.task.status !== TaskStatus.ON_HOLD) return false;
        return true;
      });
    },

    actionNeedsDate() {
      return !!this.selectedAction && NEEDS_DATE.includes(this.selectedAction.task_instruction_type_id);
    },

    canSubmitAction() {
      if (this.submittingAction) return false;
      if (!this.selectedAction) return false;
      if (this.actionNeedsDate) return !!this.actionDate;
      return true;
    },

    hasPatientInfo() {
      return this.gap.bene_phone || this.gap.bene_address_1 || this.gap.bene_city ||
             this.gap.provider_name || this.gap.language || this.gap.last_service_date;
    },

    formattedAddress() {
      const lines = [];
      if (this.gap.bene_address_1) lines.push(this.gap.bene_address_1);
      if (this.gap.bene_address_2) lines.push(this.gap.bene_address_2);
      let csz = '';
      if (this.gap.bene_city) csz += this.gap.bene_city;
      if (this.gap.bene_state) csz += (csz ? ', ' : '') + this.gap.bene_state;
      if (this.gap.bene_zip) csz += (csz ? ' ' : '') + this.gap.bene_zip;
      if (csz) lines.push(csz);
      return lines.join(', ') || null;
    },

    daysSinceDos() {
      if (!this.gap.last_service_date && !this.gap.dos) return null;
      const dosDate = new Date(this.gap.last_service_date || this.gap.dos);
      const today = new Date();
      return Math.floor((today - dosDate) / (1000 * 60 * 60 * 24));
    },

    gapFormDefaults() {
      if (!this.gap.metric_name) return null;
      return { quality_measure: this.gap.metric_name };
    },

    commentPatientIdentifier() {
      return this.gap.patient_identifier || (this.patient && this.patient.patient_identifier) || '';
    },

    isMedAdherence() {
      return this.gap.task_type_id === 14;
    },

    // Served on the gap itself by /api/quality/patient-gaps/ — no separate fetch.
    fill() {
      return this.gap.surescripts_fill || null;
    },

    medAdherenceMetadata() {
      const meta = this.gap.metadata || {};
      return {
        patient_identifier: this.gap.patient_identifier || (this.patient && this.patient.patient_identifier),
        bene_first_name: meta.bene_first_name || this.gap.bene_first_name || (this.patient && this.patient.bene_first_name),
        bene_last_name: meta.bene_last_name || this.gap.bene_last_name || (this.patient && this.patient.bene_last_name),
        bene_pcp_npi: meta.bene_pcp_npi || this.gap.bene_pcp || (this.patient && this.patient.bene_pcp),
        bene_dob: meta.bene_dob || this.gap.bene_dob || (this.patient && this.patient.bene_dob),
        med_data: meta,
      };
    }
  },
  watch: {
    'gap.task_id': {
      immediate: true,
      handler(id) {
        this.resetActionForm();
        this.task = null;
        if (id) this.fetchTask();
      },
    },
  },
  methods: {
    resetActionForm() {
      this.selectedAction = '';
      this.actionNotes = '';
      this.actionDate = '';
      this.daysSelected = null;
      this.actionError = null;
    },

    async fetchTask() {
      if (!this.gap.task_id) return;
      this.taskLoading = true;
      this.taskError = null;
      try {
        const { data } = await axios.get(`api/tasks/${this.gap.task_id}/`);
        this.task = data;
      } catch (e) {
        this.taskError = e.response?.data?.message || 'Failed to load task';
      } finally {
        this.taskLoading = false;
      }
    },

    setQuickDate(days) {
      this.daysSelected = days;
      const d = new Date();
      d.setDate(d.getDate() + days);
      d.setHours(9, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, '0');
      this.actionDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    async submitTaskAction() {
      if (!this.canSubmitAction) return;
      this.actionError = null;
      this.submittingAction = true;

      const metadata = { additionalData: this.actionNotes };
      if (this.selectedAction.task_instruction_type_id === InstructionType.SNOOZE) {
        metadata.snooze_until = new Date(this.actionDate).toISOString();
      }
      if (this.selectedAction.task_instruction_type_id === InstructionType.UPDATE_DUE_DATE) {
        metadata.due_date = new Date(this.actionDate).toISOString();
      }

      try {
        const result = await submitAction({
          task_id: this.gap.task_id,
          task_action_type_id: this.selectedAction.id,
          metadata,
        });
        if (result.already_completed) {
          this.actionError = 'This task was already completed';
          return;
        }
        this.resetActionForm();
        // The action may have advanced the opportunity to a new task, so the
        // parent must re-fetch to pick up the new task_id.
        this.$emit('refresh');
      } catch (e) {
        this.actionError = e.response?.data?.message || 'Failed to submit action';
      } finally {
        this.submittingAction = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return null;
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    getTaskTypeId() {
      return this.gap.task_type_id || 16;
    },

    onWorkSubmitted() {
      this.$emit('refresh');
    },

    onWorkDeleted() {
      this.$emit('refresh');
    },

    onCommentSaved() {
      if (this.$refs.workTimeline) {
        this.$refs.workTimeline.fetchWorkItems();
      }
      this.$emit('refresh');
    },

    copyToClipboard(text) {
      if (blockSensitiveEgress('copy')) return;
      navigator.clipboard.writeText(text);
    }
  }
};
</script>
